import React, { useState, useEffect } from "react";
import * as httpClient from "../../util/httpClient";
import { useDispatch, useSelector } from "react-redux";
import * as PasswordManagerAction from "../../store/passwordManagerService/actions";
import * as NextStepFlowAction from "../../store/nextStepFlowService/actions";
import * as PasswordManagerUtil from "../IdentityVault/PasswordManager/passwordManagerUtil";
import { ApplicationState } from "../../store";
import { Notification } from "../common/Notification";
import { useForm } from "react-hook-form";
import { getFlowByName } from "../../config/FlowName";
import RenderIcons from "../../Assets/css/_icons/partner_icon/RenderIcons";
import { useTranslation } from "react-i18next";

const AddPassword = (props: any) => {
    const [state, setState] = useState({ webSiteName: "", userNameOrEmail: "", password: "", strength: 1, webSiteUrl: "", notes: "", isFavourite: false });
    const [passwordShown, setPasswordShown] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const dispatch = useDispatch();
    const requestRequestPasswordList = () => dispatch(PasswordManagerAction.actionCreators.requestRequestPasswordList());
    const passwordManagerFlow = useSelector((state: ApplicationState) => getFlowByName(state, "CHECK-OUT-YOUR-PASSWORD-MANAGER"));
    const completeGetStartedItem = (flowID: any, flowName: string) => dispatch(NextStepFlowAction.actionCreators.completeGetStartedItem(flowID, flowName));
    const [strength, setPasswordStrength] = useState({ score: 0, message: "", color: "gray" });
    const [generatePasswordState, setGeneratePasswordState] = useState({ length: "30", digits: true, symbols: true, isClosed: true });
    const [isSaving, setisSaving] = useState(false);
    const { t: tLang } = useTranslation();

    const { clearErrors, errors, register, handleSubmit, setValue } = useForm({
        mode: "onSubmit",
        reValidateMode: "onChange",
    });

    const addNewPassword = () => {
        const requestBody = {
            WebsiteName: state.webSiteName,
            WebsiteUrl: state.webSiteUrl,
            WebsiteUsername: state.userNameOrEmail,
            WebsitePassword: state.password,
            Favorite: state.isFavourite,
            notes: state.notes,
        };

        if (state.webSiteName && state.password) {
            setisSaving(true);
            window.toggle("ap-generate-config", "ap-generate-config-btn");
            setGeneratePasswordState({ ...generatePasswordState, isClosed: !generatePasswordState.isClosed });

            httpClient
                .sendRequest("POST", `api/securepassword/setNewPassword`, requestBody)
                .then((data) => {
                    if (data.isSuccess) {
                        requestRequestPasswordList();
                        if (passwordManagerFlow && passwordManagerFlow.flowStepStatus !== "COMPLETED") {
                            completeGetStartedItem(passwordManagerFlow.flowStepId, passwordManagerFlow.flowName ? passwordManagerFlow.flowName : " ");
                        }

                        setisSaving(false);
                        Notification({
                            type: "success",
                            title: tLang("identityVault.passwordManager.addEditPassword.successText"),
                            message: tLang("identityVault.passwordManager.addEditPassword.passwordAddedMessage"),
                            duration: 5,
                        });
                        closeDialog();
                    } else {
                        setisSaving(false);
                        Notification({
                            type: "error",
                            title: tLang("identityVault.passwordManager.addEditPassword.errorText"),
                            message: tLang("identityVault.passwordManager.addEditPassword.addPasswordFailedMessage"),
                            duration: 5,
                        });
                    }
                })
                .catch(function (error) {
                    setisSaving(false);
                    Notification({
                        type: "error",
                        title: tLang("identityVault.passwordManager.addEditPassword.errorText"),
                        message: tLang("identityVault.passwordManager.addEditPassword.addPasswordFailedMessage"),
                        duration: 5,
                    });
                });
        }
    };

    const generateNewPassword = (length: number, digits: boolean, symbols: boolean) => {
        window.addToDataLayer({ event: "Generate New Password" });

        const password = PasswordManagerUtil.passwordManagerUtilFunctions.generatePassword(length, digits, symbols);

        if (password) {
            clearErrors("password");
        }
        setState({ ...state, password });
        updatePasswordStrength(password);
        setValue("password", password);
    };

    const handleChange = (event: any) => {
        const target = event.currentTarget;
        let value = target.type === "checkbox" ? target.checked : target.value;
        const name = target.name;

        if (name === "notes" && value.length > 500) {
            value = value.slice(0, 500);
        }

        if (name === "webSiteUrl" && value.length > 150) {
            value = value.slice(0, 150);
        }

        if (name === "userNameOrEmail" && value.length > 100) {
            value = value.slice(0, 100);
        }

        if (name === "password") {
            if (value.length > 64) {
                value = value.slice(0, 64);
            }

            updatePasswordStrength(value);
        }

        if (name === "webSiteName") {
            if (value) {
                clearErrors("webSiteName");
            }

            if (value.length > 64) {
                value = value.slice(0, 64);
            }
        }

        setState({
            ...state,
            [name]: value,
        });

        if (!generatePasswordState.isClosed && name === "password") {
            setGeneratePasswordState({
                ...generatePasswordState,
                ["length"]: value.length,
            });
        }
    };

    const updatePasswordStrength = (password: string) => {
        const strength = PasswordManagerUtil.passwordManagerUtilFunctions.getPasswordStrength(password, tLang);
        setPasswordStrength(strength);
    };

    const savePassword = () => {
        addNewPassword();
    };

    useEffect(() => {
        window.addEventListener("keyup", (evt: any) => {
            evt = evt || window.event;
            var isEscape = false;
            if ("key" in evt) {
                isEscape = evt.key === "Escape" || evt.key === "Esc";
            } else {
                isEscape = evt.keyCode === 27;
            }
            if (isEscape) {
                closeDialog();
            }
        });
        return () => {
            window.removeEventListener("keyup", () => {});
        };
    });

    const lengthChange = (event: any) => {
        const target = event.target;
        let value = target.type === "checkbox" ? target.checked : target.value;
        const name = target.name;

        if (parseInt(value) > 64) {
            value = "64";
        }

        setGeneratePasswordState({
            ...generatePasswordState,
            [name]: value,
        });

        generateNewPassword(parseInt(value), generatePasswordState.digits, generatePasswordState.symbols);
    };

    const closeDialog = () => {
        document.body.classList.remove("no_scroll");
        props.closeDialog();
    };

    const toggleSymbols = (event: any) => {
        const target = event.target;
        const value = target.type === "checkbox" ? target.checked : target.value;
        const name = target.name;

        setGeneratePasswordState({
            ...generatePasswordState,
            [name]: value,
        });

        generateNewPassword(parseInt(generatePasswordState.length), generatePasswordState.digits, value);
    };

    const toggleDigits = (event: any) => {
        const target = event.target;
        const value = target.type === "checkbox" ? target.checked : target.value;
        const name = target.name;

        setGeneratePasswordState({
            ...generatePasswordState,
            [name]: value,
        });
        generateNewPassword(parseInt(generatePasswordState.length), value, generatePasswordState.symbols);
    };

    const generatePasswordHandleChange = () => {
        if (generatePasswordState.isClosed) {
            window.toggle("ap-generate-config", "ap-generate-config-btn");
            setGeneratePasswordState({ ...generatePasswordState, isClosed: !generatePasswordState.isClosed });
        }

        generateNewPassword(parseInt(generatePasswordState.length), generatePasswordState.digits, generatePasswordState.symbols);
    };

    return (
        <React.Fragment>
            <div className="dialog-header">
                <div className="dialog-icon">
                    <RenderIcons iconName="key" />
                </div>
                <h1>{tLang("identityVault.passwordManager.addEditPassword.addPasswordTitle")}</h1>
            </div>

            <form onSubmit={handleSubmit(savePassword)} id="add-password-form-container">
                <div className={isSaving ? "dialog-body box loading" : "dialog-body"}>
                    <div className="form-row inline row">
                        <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                            <label htmlFor="ap-label-website-name">
                                {tLang("identityVault.passwordManager.addEditPassword.label.name")}{" "}
                                <span className="required" title="Mandatory">
                                    *
                                </span>
                            </label>
                        </div>
                        <div role="alert" className="form-input col-xs-12 col-sm-8 col-no-gutter">
                            <input
                                aria-required="true"
                                type="text"
                                name="webSiteName"
                                defaultValue={state.webSiteName ? state.webSiteName : ""}
                                maxLength={64}
                                className={submitted && !state.webSiteName ? "inputbox status-error" : "inputbox"}
                                id="ap-label-website-name"
                                placeholder=""
                                onChange={handleChange}
                                ref={register({
                                    required: true,
                                })}
                            />

                            {errors.webSiteName && errors.webSiteName.type === "required" && (
                                <span id="error-ap-website" className="form-error" aria-live="polite" aria-atomic="true">
                                    <p>{tLang("identityVault.passwordManager.validation.wbsiteNameRequired")}</p>
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="form-row inline row">
                        <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                            <label htmlFor="ap-label-username-email">{tLang("identityVault.passwordManager.addEditPassword.label.username")}</label>
                        </div>
                        <div className="form-input col-xs-12 col-sm-8 col-no-gutter">
                            <input
                                type="text"
                                value={state.userNameOrEmail}
                                name="userNameOrEmail"
                                maxLength={100}
                                className="inputbox"
                                id="ap-label-username-email"
                                placeholder=""
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div className="form-row inline row">
                        <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                            <label htmlFor="ap-label-password">
                                {tLang("identityVault.passwordManager.addEditPassword.label.password")}{" "}
                                <span className="required" title="Mandatory">
                                    *
                                </span>
                            </label>
                        </div>
                        <div className="form-input col-xs-12 col-sm-8 col-no-gutter">
                            <div className="row">
                                <div role="alert" className="col-xs col-no-gutter form-input">
                                    <input
                                        aria-required="true"
                                        type={passwordShown ? "text" : "password"}
                                        maxLength={64}
                                        defaultValue={state.password ? state.password : ""}
                                        name="password"
                                        className={submitted && !state.password ? "inputbox status-error" : "inputbox"}
                                        id="ap-label-password"
                                        placeholder=""
                                        onChange={handleChange}
                                        ref={register({
                                            required: true,
                                        })}
                                    />

                                    {errors.password && errors.password.type === "required" && (
                                        <span id="error-ap-password" className="form-error" aria-live="polite" aria-atomic="true">
                                            <p>{tLang("identityVault.passwordManager.validation.passwordRequired")}</p>
                                        </span>
                                    )}
                                    <div className="form-input-button">
                                        <button
                                            type="button"
                                            className="btn-default btn-icon-only btn-no-padding"
                                            onClick={() => {
                                                setPasswordShown(!passwordShown);
                                                window.addToDataLayer({ event: "Show / Hide Password Text" });
                                            }}
                                            title={
                                                passwordShown
                                                    ? tLang("identityVault.passwordManager.addEditPassword.hidePwdAddPwdPopUp")
                                                    : tLang("identityVault.passwordManager.addEditPassword.shwPwdAddPwdPopUp")
                                            }
                                        >
                                            <span className="btn-icon">
                                                <em
                                                    className={`las ${passwordShown ? "la-eye-slash" : "la-eye"
                                                        }`}
                                                ></em>
                                            </span>
                                            <span className="btn-text">Show / Hide Password Text</span>
                                        </button>
                                    </div>
                                </div>

                                <div className="col-xs flex-grow-null col-no-gutter">
                                    <button
                                        type="button"
                                        className="btn-default btn-primary-outline margin-left-xs"
                                        id="ap-generate-config-btn"
                                        onClick={generatePasswordHandleChange}
                                    >
                                        <span className="btn-toggle-status sr-only">Show</span>
                                        <span className="btn-text">{tLang("identityVault.passwordManager.addEditPassword.generateBtnText")}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div id="ap-generate-config" className="drawer-content" style={{ display: "none" }}>
                        <div className="row">
                            <div className="col-xs col-sm-8 col-sm-offset-4 col-no-gutter">
                                <div className="generate-confiq">
                                    <fieldset>
                                        <legend className="sr-only">Set Attributes for your generated password</legend>
                                        <div className={errors["length"] ? "form-row row status-error" : "form-row row"}>
                                            <div className="form-row inline row">
                                                <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                                                    <label htmlFor="ap-label-pw-length">
                                                        {tLang("identityVault.passwordManager.addEditPassword.label.length")}
                                                    </label>
                                                </div>
                                                <div role="alert" className="form-input col-xs-12 col-sm-4 col-no-gutter">
                                                    <input
                                                        aria-required="true"
                                                        type="number"
                                                        className="inputbox"
                                                        id="ap-label-pw-length"
                                                        placeholder=""
                                                        onChange={lengthChange}
                                                        name="length"
                                                        onInput={lengthChange}
                                                        value={generatePasswordState.length}
                                                        step="1"
                                                        size={4}
                                                        ref={register({
                                                            max: 64,
                                                            min: 1,
                                                        })}
                                                    />
                                                    {errors.length && errors.length.type === "max" && (
                                                        <span className="form-error">{tLang("identityVault.passwordManager.validation.paswordLength2")}</span>
                                                    )}

                                                    {errors.length && errors.length.type === "min" && (
                                                        <span className="form-error">{tLang("identityVault.passwordManager.validation.passwordLength")}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <fieldset className="form-row inline">
                                            <div className="row">
                                                <div className="col-xs-12 col-sm-4 col-no-gutter">
                                                    <legend className="legend-label"> <label className="form-toggle-label" htmlFor="ap-label-pw-digits">{tLang("identityVault.passwordManager.addEditPassword.label.digits")}</label></legend>
                                                </div>

                                                <div className="col-xs-12 col-sm-8 col-no-gutter">
                                                    <div className="form-row">
                                                        <div className="form-input">
                                                            <label className="form-toggle-label" htmlFor="ap-label-pw-digits">
                                                                <input
                                                                    type="checkbox"
                                                                    id="ap-label-pw-digits"
                                                                    checked={generatePasswordState.digits}
                                                                    name="digits"
                                                                    onChange={toggleDigits}
                                                                />
                                                                <span className="form-toggle-label-text">
                                                                    <span className="sr-only">Include Digits</span>
                                                                </span>
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </fieldset>

                                        <fieldset className="form-row no-margin inline">
                                            <div className="row">
                                                <div className="col-xs-12 col-sm-4 col-no-gutter">
                                                    <legend className="legend-label"> <label className="form-toggle-label" htmlFor="ap-label-pw-symbols">{tLang("identityVault.passwordManager.addEditPassword.label.symbols")}</label></legend>
                                                </div>

                                                <div className="col-xs-12 col-sm-8 col-no-gutter">
                                                    <div className="form-row">
                                                        <div className="form-input">
                                                            <label className="form-toggle-label" htmlFor="ap-label-pw-symbols">
                                                                <input
                                                                    type="checkbox"
                                                                    id="ap-label-pw-symbols"
                                                                    checked={generatePasswordState.symbols}
                                                                    name="symbols"
                                                                    onChange={toggleSymbols}
                                                                />
                                                                <span className="form-toggle-label-text">
                                                                    <span className="sr-only">Include Symbols</span>
                                                                </span>
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </fieldset>
                                    </fieldset>
                                </div>
                            </div>
                        </div>
                    </div>

                    <fieldset className="form-row inline">
                        <div className="row">
                            <div className="col-xs-12 col-sm-4 col-no-gutter">
                                <legend className="legend-label">{tLang("identityVault.passwordManager.addEditPassword.label.strength")}</legend>
                            </div>

                            <div className="col-xs-12 col-sm-8 col-no-gutter">
                                <span id="ap-passwordmeter" className="passwordmeter">
                                    <span style={{ width: (strength.score + 1) * 25 + "px", backgroundColor: strength.color }}></span>
                                </span>
                                <span id="ap-passwordmessage" className="passwordmessage" aria-live="polite">
                                    <strong>{strength.message}</strong>
                                </span>
                            </div>
                        </div>
                    </fieldset>

                    <div className="form-row inline row">
                        <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                            <label htmlFor="ap-label-website-url">{tLang("identityVault.passwordManager.addEditPassword.label.website")}</label>
                        </div>
                        <div role="alert" className="form-input col-xs-12 col-sm-8 col-no-gutter">
                            <input
                                aria-required="true"
                                type="text"
                                value={state.webSiteUrl}
                                name="webSiteUrl"
                                maxLength={150}
                                className="inputbox"
                                id="ap-label-website-url"
                                placeholder=""
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div className="form-row inline row">
                        <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                            <label htmlFor="ap-label-notes">{tLang("identityVault.passwordManager.addEditPassword.label.notes")}</label>
                        </div>
                        <div role="alert" className="form-input col-xs-12 col-sm-8 col-no-gutter">
                            <textarea
                                aria-required="true"
                                value={state.notes}
                                name="notes"
                                className="inputbox"
                                id="ap-label-notes"
                                maxLength={500}
                                placeholder={tLang("identityVault.passwordManager.placeholder.note")}
                                onChange={handleChange}
                                rows={6}
                            ></textarea>
                        </div>
                    </div>

                    <div className="form-row row">
                        <div className="col-xs-12 col-sm-offset-4 col-no-gutter">
                            <div className="form-input">
                                <label className="form-checkbox-label" htmlFor="ap-mark-password-favourite">
                                    <input type="checkbox" name="isFavourite" onChange={handleChange} checked={state.isFavourite} id="ap-mark-password-favourite" />
                                    <span className="form-checkbox-label-text">{tLang("identityVault.passwordManager.addEditPassword.label.markAsfvt")}</span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="dialog-footer">
                    <div className="row between-xs">
                        <div className="col-xs col-no-gutter flex-grow-null">
                            <button
                                type="button"
                                className="btn-default btn-link btn-no-padding btn-no-border"
                                onClick={() => {
                                    closeDialog();
                                    window.addToDataLayer({ event: "Cancel Adding New Password" });
                                }}
                            >
                                <span className="btn-text">{tLang("identityVault.passwordManager.addEditPassword.cancelBtnText")}</span>
                            </button>
                        </div>
                        <div className="col-xs col-no-gutter flex-grow-null">
                            <button
                                type="submit"
                                className="btn-default btn-primary"
                                onClick={() => {
                                    setSubmitted(true);
                                    window.addToDataLayer({ event: "Save New Password" });
                                }}
                            >
                                <span className="btn-text">{tLang("identityVault.passwordManager.addEditPassword.saveBtnText")}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </React.Fragment>
    );
};

export default AddPassword;
